import React, { useCallback, useEffect, useState } from "react";
import {
  Building2,
  CalendarClock,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquare,
  Paperclip,
  Save,
  Tag,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../Modal/Modal";
import Input from "../Input/Input";

/* ===================== SERVICES ===================== */
import { apiGet, apiPatch } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./HelpDeskShared.module.scss";

const STATUS_OPTIONS = [
  { label: "Open", value: "Open" },
  { label: "In Progress", value: "InProgress" },
  { label: "Resolved", value: "Resolved" },
  { label: "Closed", value: "Closed" },
];

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ViewTicketModal = ({
  open,
  ticketId,
  onClose,
  onError,
  onSuccess,
  canUpdateStatus = false,
  orgMap = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState("");

  const fetchTicket = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await apiGet("catalog", `/help-desk/tickets/${ticketId}`);
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load ticket",
          }),
        );
      }
      setTicket(res.data || null);
      setStatus(res.data?.status || "");
    } catch (err) {
      if (typeof onError === "function") {
        onError(getErrorMessage(err, "Failed to load ticket"));
      }
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [ticketId, onError, onClose]);

  useEffect(() => {
    if (open) {
      setTicket(null);
      setStatus("");
      fetchTicket();
    }
  }, [open, fetchTicket]);

  const handleStatusSave = async () => {
    if (!ticketId || !status || status === ticket?.status) return;
    setSubmitting(true);
    try {
      const res = await apiPatch(
        "catalog",
        `/help-desk/tickets/${ticketId}/status`,
        { status },
      );
      if (res?.status === "success") {
        onSuccess?.(res?.message || "Status updated");
        onClose?.();
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update status",
          }),
        );
      }
    } catch (err) {
      onError?.(getErrorMessage(err, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isInternalUser = localStorage.getItem("userType") === "Internal";

  const statusTone =
    ticket?.status === "Open"
      ? "open"
      : ticket?.status === "InProgress"
        ? "progress"
        : ticket?.status === "Resolved"
          ? "resolved"
          : "closed";

  return (
    <Modal title="Ticket Details" onClose={onClose}>
      {loading ? (
        <div className={styles["helpdesk-modal__loading"]}>
          <Loader2 className={styles["helpdesk-modal__spin"]} size={28} />
          <span>Loading ticket…</span>
        </div>
      ) : ticket ? (
        <div className={styles["helpdesk-modal__container"]}>
          {/* SUMMARY */}
          <div className={styles["helpdesk-modal__section"]}>
            <div className={styles["helpdesk-modal__section-header"]}>
              <div className={styles["helpdesk-modal__icon-wrapper"]}>
                <Tag size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-modal__section-title"]}>
                {ticket.ticketNo || "Ticket"}
              </h3>
              <span
                className={`${styles["helpdesk-modal__status-badge"]} ${
                  styles[`helpdesk-modal__status-badge--${statusTone}`]
                }`}
              >
                {ticket.status || "--"}
              </span>
            </div>

            <h4 className={styles["helpdesk-modal__subject"]}>
              {ticket.subject || "--"}
            </h4>
            <p className={styles["helpdesk-modal__description"]}>
              {ticket.description || "No description provided."}
            </p>
          </div>

          {/* META GRID */}
          <div className={styles["helpdesk-modal__section"]}>
            <div className={styles["helpdesk-modal__section-header"]}>
              <div className={styles["helpdesk-modal__icon-wrapper"]}>
                <Building2 size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-modal__section-title"]}>
                Context
              </h3>
            </div>
            <div className={styles["helpdesk-modal__grid"]}>
              <Field label="Product" value={ticket.productName} />
              {isInternalUser && (
                <Field
                  label="Organization"
                  value={orgMap?.[ticket.orgId] || ticket.orgId}
                />
              )}
              {ticket.productUrl && (
                <div className={styles["helpdesk-modal__field"]}>
                  <span className={styles["helpdesk-modal__field-label"]}>
                    Product URL
                  </span>
                  <a
                    href={ticket.productUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles["helpdesk-modal__link"]}
                  >
                    <ExternalLink size={14} strokeWidth={2.5} />
                    {ticket.productUrl}
                  </a>
                </div>
              )}
              {ticket.attachmentUrl && (
                <div className={styles["helpdesk-modal__field"]}>
                  <span className={styles["helpdesk-modal__field-label"]}>
                    Attachment
                  </span>
                  <a
                    href={ticket.attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles["helpdesk-modal__link"]}
                  >
                    <Paperclip size={14} strokeWidth={2.5} />
                    {ticket.attachmentName || "Open attachment"}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* SLA / TIMELINE */}
          <div className={styles["helpdesk-modal__section"]}>
            <div className={styles["helpdesk-modal__section-header"]}>
              <div className={styles["helpdesk-modal__icon-wrapper"]}>
                <CalendarClock size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-modal__section-title"]}>
                Timeline
              </h3>
            </div>
            <div className={styles["helpdesk-modal__grid"]}>
              <Field label="SLA Hours" value={ticket.slaHours} />
              <Field label="Due At" value={formatDateTime(ticket.dueAt)} />
              <Field
                label="Resolved At"
                value={formatDateTime(ticket.resolvedAt)}
              />
              <Field label="Created" value={formatDateTime(ticket.createdAt)} />
              <Field
                label="Last Updated"
                value={formatDateTime(ticket.updatedAt)}
              />
            </div>
          </div>

          {/* STATUS UPDATE (admin only) */}
          {canUpdateStatus && (
            <div className={styles["helpdesk-modal__section"]}>
              <div className={styles["helpdesk-modal__section-header"]}>
                <div className={styles["helpdesk-modal__icon-wrapper"]}>
                  <MessageSquare size={18} strokeWidth={2.5} />
                </div>
                <h3 className={styles["helpdesk-modal__section-title"]}>
                  Update Status
                </h3>
              </div>

              <div className={styles["helpdesk-modal__status-row"]}>
                <Input
                  type="select"
                  label="Status"
                  name="status"
                  value={status}
                  options={STATUS_OPTIONS}
                  onChange={(e) => setStatus(e.target.value)}
                />
                <button
                  type="button"
                  className={`${styles["helpdesk-modal__btn"]} ${styles["helpdesk-modal__btn--primary"]}`}
                  onClick={handleStatusSave}
                  disabled={submitting || !status || status === ticket?.status}
                >
                  <Save size={16} strokeWidth={2.5} />
                  {submitting ? "Updating…" : "Save Status"}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className={styles["helpdesk-modal__empty"]}>
          <FileText size={16} strokeWidth={2.5} />
          Ticket not found.
        </p>
      )}
    </Modal>
  );
};

const Field = ({ label, value }) => (
  <div className={styles["helpdesk-modal__field"]}>
    <span className={styles["helpdesk-modal__field-label"]}>{label}</span>
    <span className={styles["helpdesk-modal__field-value"]}>
      {value || value === 0 ? String(value) : "--"}
    </span>
  </div>
);

export default ViewTicketModal;
